import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  ShoppingCart, Search, Plus, Minus, Trash2, User,
  CreditCard, X, Loader2, Pill, Package
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import ProductUnitSelector from '../components/ProductUnitSelector';

const POS = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('products');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clients, setClients] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('percent');
  const [showClearCartModal, setShowClearCartModal] = useState(false);
  const [showUnitSelector, setShowUnitSelector] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState('full');

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, servicesRes, clientsRes] = await Promise.all([
        api.get('/inventory/products'),
        api.get('/inventory/services'),
        api.get('/clients', { params: { limit: 100 } })
      ]);
      setProducts(productsRes.data.filter(p => p.quantity_in_stock > 0));
      setServices(servicesRes.data.filter(s => s.is_active));
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
    } catch (error) {
      toast.error('Failed to load data');
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const searchClients = async (query) => {
    if (query.length < 2) return;
    try {
      const res = await api.get('/clients', {
        params: { search: query, limit: 5 }
      });
      setClients(res.data.clients || []);
    } catch (error) {
      console.error('Client search failed');
    }
  };

  const handleProductClick = (product) => {
    if (product.allow_partial_sale && product.total_units_per_package > 1) {
      setSelectedProduct(product);
      setShowUnitSelector(true);
    } else {
      addToCart(product, 'product');
    }
  };

  const addToCart = (item, type, unitData = null) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id && i.type === type && i.isPartial === !!unitData);
      if (existing) {
        return prev.map(i =>
          i.id === item.id && i.type === type && i.isPartial === !!unitData
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      const itemPrice = type === 'product' 
        ? (unitData ? unitData.display_price : parseFloat(item.selling_price))
        : parseFloat(item.price || 0);
      
      const cartItem = {
        id: item.id,
        name: item.name,
        price: itemPrice,
        quantity: 1,
        type,
        unitType: item.unit_type || null,
        totalUnits: item.total_units_per_package || null,
        allowPartial: item.allow_partial_sale || false
      };

      if (unitData) {
        cartItem.isPartial = unitData.is_partial_sale;
        cartItem.units = unitData.units_requested;
        cartItem.displayLabel = unitData.display_label;
      }

      return [...prev, cartItem];
    });
  };

  const removeFromCart = (id, type, units = null) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === id && i.type === type && i.isPartial === !!units);
      if (existing && existing.quantity > 1) {
        return prev.map(i =>
          i.id === id && i.type === type && i.isPartial === !!units
            ? { ...i, quantity: i.quantity - 1 }
            : i
        );
      }
      return prev.filter(i => !(i.id === id && i.type === type && i.isPartial === !!units));
    });
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateDiscount = () => {
    const subtotal = calculateSubtotal();
    if (discountType === 'percent') {
      return subtotal * (discount / 100);
    }
    return discount;
  };

  const calculateTotal = () => {
    return Math.max(0, calculateSubtotal() - calculateDiscount());
  };

  const handleProceedToPayment = (paymentType) => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    navigate('/payment', {
      state: {
        cart,
        discount,
        discountType,
        subtotal: calculateSubtotal(),
        discountAmount: calculateDiscount(),
        total: calculateTotal(),
        client: selectedClient,
        paymentType
      }
    });
  };

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredServices = services.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-full">
      {/* Left Side - Products/Services */}
      <div className="flex-1 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-brand-blue-100 rounded-lg">
              <ShoppingCart size={24} className="text-brand-blue" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-neutral-dark">Point of Sale</h1>
              <p className="text-sm text-neutral-gray">Select items to add to cart</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-gray" />
          <input
            type="text"
            placeholder="Search products or services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white"
          />
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-neutral-light rounded-xl p-1">
          <button
            onClick={() => setActiveTab('products')}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'products' ? 'bg-white text-neutral-dark shadow-sm' : 'text-neutral-gray hover:text-neutral-dark'
            }`}
          >
            Products
          </button>
          <button
            onClick={() => setActiveTab('services')}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'services' ? 'bg-white text-neutral-dark shadow-sm' : 'text-neutral-gray hover:text-neutral-dark'
            }`}
          >
            Services
          </button>
        </div>

        {/* Items Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[calc(100vh-350px)] overflow-y-auto">
          {activeTab === 'products' ? (
            filteredProducts.length === 0 ? (
              <p className="col-span-2 text-center py-8 text-neutral-gray">No products found</p>
            ) : filteredProducts.map(product => (
              <button
                key={product.id}
                onClick={() => handleProductClick(product)}
                className="w-full text-left p-3 rounded-xl border border-neutral-medium hover:border-brand-green hover:bg-brand-green-50/30 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-dark group-hover:text-brand-green truncate">
                      {product.name}
                    </p>
                    <p className="text-xs text-neutral-gray mt-0.5">
                      Stock: {product.quantity_in_stock} {product.unit_type || 'units'}
                    </p>
                    {product.allow_partial_sale && (
                      <span className="text-xs bg-brand-blue-50 text-brand-blue px-1.5 py-0.5 rounded-full inline-flex items-center mt-1">
                        <Pill size={10} className="mr-0.5" />
                        Per unit sale
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-bold text-brand-green ml-3 whitespace-nowrap">
                    KES {parseFloat(product.selling_price).toLocaleString()}
                  </span>
                </div>
              </button>
            ))
          ) : (
            filteredServices.length === 0 ? (
              <p className="col-span-2 text-center py-8 text-neutral-gray">No services found</p>
            ) : filteredServices.map(service => (
              <button
                key={service.id}
                onClick={() => addToCart(service, 'service')}
                className="w-full text-left p-3 rounded-xl border border-neutral-medium hover:border-brand-blue hover:bg-brand-blue-50/30 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-dark group-hover:text-brand-blue truncate">
                      {service.name}
                    </p>
                    <p className="text-xs text-brand-blue mt-0.5">Service</p>
                  </div>
                  <span className="text-sm font-bold text-brand-blue ml-3 whitespace-nowrap">
                    KES {parseFloat(service.price).toLocaleString()}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Right Side - Cart */}
      <div className="w-full lg:w-96 bg-white rounded-xl shadow-sm border border-neutral-medium p-4 flex flex-col">
        {/* Client Selection */}
        <div className="mb-4">
          <label className="block text-xs font-medium text-neutral-gray mb-1.5">Client</label>
          <select
            value={selectedClient?.id || 'walk-in'}
            onChange={(e) => {
              if (e.target.value === 'walk-in') {
                setSelectedClient(null);
              } else {
                const client = clients.find(c => c.id === e.target.value);
                setSelectedClient(client || null);
              }
            }}
            className="w-full px-3 py-2 border border-neutral-medium rounded-lg text-sm focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white"
          >
            <option value="walk-in">Walk-in Client</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.first_name} {client.last_name} {client.phone ? `(${client.phone})` : ''}
              </option>
            ))}
          </select>
          {clients.length === 0 && (
            <p className="text-xs text-neutral-gray mt-1">No registered clients found</p>
          )}
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto mb-4" style={{ maxHeight: '300px' }}>
          {cart.length === 0 ? (
            <div className="text-center py-6">
              <ShoppingCart size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-xs text-neutral-gray">Cart is empty</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {cart.map((item, index) => (
                <div key={`${item.id}-${item.type}-${item.isPartial || false}-${index}`} className="flex items-center justify-between p-2 rounded-lg border border-neutral-medium hover:bg-gray-50">
                  <div className="flex-1 min-w-0 mr-2">
                    <p className="text-xs font-medium text-neutral-dark truncate">{item.name}</p>
                    <p className="text-[10px] text-neutral-gray">
                      KES {(item.price || 0).toLocaleString()} × {item.quantity}
                      {item.isPartial && (
                        <span className="text-[10px] bg-brand-blue-50 text-brand-blue px-1 py-0.5 rounded-full ml-1">
                          {item.units} {item.unitType || 'units'}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className="text-xs font-semibold text-neutral-dark whitespace-nowrap">
                      KES {((item.price || 0) * item.quantity).toLocaleString()}
                    </span>
                    <button
                      onClick={() => removeFromCart(item.id, item.type, item.isPartial ? item.units : null)}
                      className="p-1 text-neutral-gray hover:text-brand-red hover:bg-brand-red-50 rounded"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Discount */}
        {cart.length > 0 && (
          <div className="mb-4 p-3 bg-neutral-light rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <input
                type="number"
                value={discount}
                onChange={(e) => setDiscount(Math.max(0, parseFloat(e.target.value) || 0))}
                className="w-20 px-2 py-1 border border-neutral-medium rounded text-sm"
                min="0"
              />
              <select
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value)}
                className="px-2 py-1 border border-neutral-medium rounded text-sm bg-white"
              >
                <option value="percent">%</option>
                <option value="fixed">KES</option>
              </select>
              <span className="text-xs text-neutral-gray">Discount</span>
            </div>
            {calculateDiscount() > 0 && (
              <div className="flex justify-between text-sm text-brand-red">
                <span>Discount ({discountType === 'percent' ? `${discount}%` : 'KES'})</span>
                <span>-KES {calculateDiscount().toLocaleString()}</span>
              </div>
            )}
          </div>
        )}

        {/* Totals */}
        {cart.length > 0 && (
          <div className="space-y-2 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-gray">Subtotal</span>
              <span className="text-neutral-dark">KES {calculateSubtotal().toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t border-neutral-medium pt-2">
              <span className="text-neutral-dark">Total</span>
              <span className="text-brand-green">KES {calculateTotal().toLocaleString()}</span>
            </div>
          </div>
        )}

        {/* Payment Type Selection */}
        {cart.length > 0 && (
          <div className="mb-4 p-3 bg-neutral-light rounded-lg">
            <label className="block text-xs font-medium text-neutral-gray mb-2">Payment Type</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setSelectedPaymentType('full')}
                className={`p-2 rounded-lg border-2 transition-all ${
                  selectedPaymentType === 'full'
                    ? 'border-brand-green bg-brand-green-50'
                    : 'border-neutral-medium hover:border-gray-300'
                }`}
              >
                <p className="text-xs font-medium text-neutral-dark">Full</p>
                <p className="text-[10px] text-neutral-gray">Walk-in OK</p>
              </button>
              <button
                onClick={() => setSelectedPaymentType('partial')}
                disabled={!selectedClient}
                className={`p-2 rounded-lg border-2 transition-all ${
                  selectedPaymentType === 'partial'
                    ? 'border-brand-blue bg-brand-blue-50'
                    : selectedClient
                      ? 'border-neutral-medium hover:border-gray-300'
                      : 'border-gray-200 opacity-50 cursor-not-allowed'
                }`}
              >
                <p className="text-xs font-medium text-neutral-dark">Partial</p>
                <p className="text-[10px] text-neutral-gray">Client required</p>
              </button>
              <button
                onClick={() => setSelectedPaymentType('credit')}
                disabled={!selectedClient}
                className={`p-2 rounded-lg border-2 transition-all ${
                  selectedPaymentType === 'credit'
                    ? 'border-brand-orange bg-brand-orange-50'
                    : selectedClient
                      ? 'border-neutral-medium hover:border-gray-300'
                      : 'border-gray-200 opacity-50 cursor-not-allowed'
                }`}
              >
                <p className="text-xs font-medium text-neutral-dark">Credit</p>
                <p className="text-[10px] text-neutral-gray">Client required</p>
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-2">
          {cart.length > 0 && (
            <>
              <button
                onClick={() => handleProceedToPayment(selectedPaymentType)}
                disabled={!selectedClient && selectedPaymentType !== 'full'}
                className={`w-full py-3 rounded-xl font-medium transition-colors ${
                  selectedClient || selectedPaymentType === 'full'
                    ? 'btn-primary'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                {selectedClient ? `Proceed (${selectedPaymentType})` : selectedPaymentType === 'full' ? 'Proceed (Full Payment)' : 'Select Client for Partial/Credit'}
              </button>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleProceedToPayment('credit')}
                  disabled={!selectedClient}
                  className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    selectedClient ? 'bg-brand-orange text-white hover:bg-brand-orange-700' : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Credit Sale
                </button>
                <button
                  onClick={() => setShowClearCartModal(true)}
                  className="px-4 py-2.5 border border-neutral-medium rounded-xl text-sm font-medium text-neutral-gray hover:bg-neutral-light"
                >
                  Clear
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Client Search Modal */}
      {showClientSearch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-neutral-medium flex items-center justify-between">
              <h2 className="text-lg font-semibold text-neutral-dark">Select Client</h2>
              <button onClick={() => setShowClientSearch(false)} className="p-1 hover:bg-neutral-light rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-4">
              <input
                type="text"
                placeholder="Search clients..."
                value={clientSearch}
                onChange={(e) => { setClientSearch(e.target.value); searchClients(e.target.value); }}
                className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green mb-4"
              />
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {clients.map(client => (
                  <button
                    key={client.id}
                    onClick={() => { setSelectedClient(client); setShowClientSearch(false); }}
                    className="w-full text-left p-3 rounded-lg border border-neutral-medium hover:bg-neutral-light transition-colors"
                  >
                    <p className="text-sm font-medium text-neutral-dark">{client.first_name} {client.last_name}</p>
                    <p className="text-xs text-neutral-gray">{client.phone || client.email}</p>
                  </button>
                ))}
                {clients.length === 0 && clientSearch.length >= 2 && (
                  <p className="text-sm text-neutral-gray text-center py-4">No clients found</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clear Cart Confirmation */}
      {showClearCartModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-neutral-dark mb-2">Clear Cart</h3>
            <p className="text-sm text-neutral-gray mb-4">Are you sure you want to clear all items from the cart?</p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setShowClearCartModal(false)}
                className="px-4 py-2 border border-neutral-medium rounded-xl text-sm font-medium text-gray-700 hover:bg-neutral-light">Cancel</button>
              <button onClick={() => { setCart([]); setDiscount(0); setShowClearCartModal(false); }}
                className="px-4 py-2 bg-brand-red text-white rounded-xl text-sm font-medium hover:bg-brand-red-700">Clear Cart</button>
            </div>
          </div>
        </div>
      )}

      {/* Product Unit Selector Modal */}
      {showUnitSelector && selectedProduct && (
        <ProductUnitSelector
          product={selectedProduct}
          onAddToCart={(unitData) => {
            addToCart(selectedProduct, 'product', unitData);
            setShowUnitSelector(false);
            setSelectedProduct(null);
          }}
          onClose={() => {
            setShowUnitSelector(false);
            setSelectedProduct(null);
          }}
        />
      )}
    </div>
  );
};

export default POS;
